import { useSyncExternalStore } from "react";

/** Payload of one `lead:recaptured` socket push (server targets it at the
 *  lead's active assignees only — if it reached us, it's ours). */
export interface RecaptureAlertPayload {
  leadId: number;
  leadName?: string | null;
  phone?: string | null;
  sourceName?: string | null;
  reenquiryCount?: number;
  timestamp: string;
}

/**
 * Burst coalescer — an hourly Meta sync can mark several re-enquiries within
 * seconds; popping N stacked modals would be hostile. Events collect for
 * COALESCE_MS after the first arrival, then surface ONCE as a single batch
 * modal. Same pattern as the notification-tone popup (`toneAlert.ts`).
 */
const COALESCE_MS = 3000;

let queue: RecaptureAlertPayload[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

// Currently displayed popup batch (null = popup closed).
let currentBatch: RecaptureAlertPayload[] | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

function flush() {
  timer = null;
  // Dedupe by leadId — a re-enquiry can arrive twice in one burst.
  const seen = new Set<number>();
  const batch = queue.filter((p) => {
    if (seen.has(p.leadId)) return false;
    seen.add(p.leadId);
    return true;
  });
  queue = [];
  if (batch.length === 0) return;

  // If a batch is already showing, merge instead of replacing silently.
  if (currentBatch?.length) {
    const existing = new Set(currentBatch.map((p) => p.leadId));
    currentBatch = [
      ...currentBatch,
      ...batch.filter((p) => !existing.has(p.leadId)),
    ];
  } else {
    currentBatch = batch;
  }
  notify();
}

/** Socket handler entry point. */
export function enqueueRecaptureAlert(
  payload: Omit<RecaptureAlertPayload, "leadId"> & { leadId?: number },
) {
  if (!payload?.leadId) return;
  queue.push(payload as RecaptureAlertPayload);
  if (!timer) timer = setTimeout(flush, COALESCE_MS);
}

export function dismissRecaptureAlert() {
  currentBatch = null;
  notify();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSnapshot = () => currentBatch;

/** Reactive view of the popup batch for RecaptureAlertHost. */
export function useRecaptureAlertBatch(): RecaptureAlertPayload[] | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
